import React, { useMemo, useState } from 'react';
import { useFinalClaims } from '../hook/useFinalClaims.mjs';
import { money } from '../util/format.mjs';
import { claimTotals, claimsByRegion, claimsByPosition } from '../util/aggregate.mjs';
import { periodText as periodTextOf, periodTag, namedRegion } from '../util/period.mjs';
import { exportBreakdown } from '../util/reportsExcel.mjs';
import {
  ScopeToolbar, StatTiles, LoadingCard, ErrorCard, Section, EmptyRow, TH, THR, TD, TDR,
} from './ReportParts.mjs';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** One grouped table: region or job position, with the same money columns. */
const GroupTable = ({ label, rows, nameOf, emptyText, totals }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-stanford-red text-white">
        <tr>
          <th className={TH}>{label}</th>
          <th className={THR}>Claimants</th>
          <th className={THR}>Claims</th>
          <th className={THR}>Per diem</th>
          <th className={THR}>Expense</th>
          <th className={THR}>Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={nameOf(r)} className="border-b border-gray-100 hover:bg-gray-50">
            <td className={`${TD} font-medium text-gray-800`}>{r.label}</td>
            <td className={TDR}>{r.claimants}</td>
            <td className={TDR}>{r.claims}</td>
            <td className={TDR}>{money(r.perDiem)}</td>
            <td className={TDR}>{money(r.expense)}</td>
            <td className={`${TDR} font-medium text-gray-800`}>{money(r.total)}</td>
          </tr>
        ))}
        {rows.length === 0 && <EmptyRow colSpan={6} text={emptyText} />}
      </tbody>
      {rows.length > 1 && (
        <tfoot>
          <tr className="bg-gray-50 font-semibold">
            <td className={TD}>Total</td>
            <td className={TDR}>{totals.claimants}</td>
            <td className={TDR}>{totals.claims}</td>
            <td className={TDR}>{money(totals.perDiem)}</td>
            <td className={TDR}>{money(totals.expense)}</td>
            <td className={TDR}>{money(totals.total)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

/**
 * Approved spend for the directors, regional directors and admins: the
 * period's FINAL claims, by region and by job position.
 *
 * A claim counts once it has reached its final approval - a staff claim when
 * the monthly report holding it is finally approved, a contractor's when the
 * auditor gives the last approval. Nothing in draft or still in review is
 * here; that is followed on the dashboards.
 */
const BreakdownTab = ({ scope }) => {
  const { data, claims, loading, error, fiscalYears, reload } = useFinalClaims(scope);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  // Global roles see every region in scope, zeros included, or the one they
  // picked; a regional role sees just their own, which is all the server
  // sends them.
  const regionList = useMemo(() => {
    if (scope.region !== 'all') return [scope.region];
    if (!scope.isGlobal) return data?.region && data.region !== 'all' ? [data.region] : [];
    return data?.regions || [];
  }, [scope.region, scope.isGlobal, data]);

  const totals = useMemo(() => claimTotals(claims), [claims]);
  const byRegion = useMemo(
    () => claimsByRegion(claims, regionList).map(r => ({ ...r, label: r.region })),
    [claims, regionList]
  );
  const byPosition = useMemo(() => claimsByPosition(claims).map(p => ({
    ...p,
    label: p.recorded ? p.position : <span className="text-gray-500 italic">{p.position}</span>,
  })), [claims]);

  if (!data && loading) return <LoadingCard text="Loading approved spend..." />;
  if (error && !data) return <ErrorCard error={`Could not load approved spend: ${error}`} onRetry={reload} />;

  const periodText = periodTextOf(scope.fy, scope.quarter);
  const fileRegion = namedRegion(scope, data?.region);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportBreakdown({
        claims, regions: regionList, fyLabel: periodText, fyShort: periodTag(scope.fy, scope.quarter), region: fileRegion,
      });
    } catch (err) {
      setExportError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <ScopeToolbar
        scope={scope}
        fiscalYears={fiscalYears}
        onExport={handleExport}
        exporting={exporting}
        exportDisabled={claims.length === 0}
        loading={loading}
        note="Final claims only: staff claims in a finally approved monthly report, and contractor claims the auditor has given final approval. Nothing in draft or still in review is counted."
      />
      {(error || exportError) && (
        <div className="mb-4"><ErrorCard error={exportError || `Could not refresh: ${error}`} /></div>
      )}

      <StatTiles tiles={[
        { label: 'Approved spend', value: money(totals.total), hint: `Per diem ${money(totals.perDiem)} · Expense ${money(totals.expense)}` },
        { label: 'Claims', value: String(totals.claims), hint: `${plural(totals.perDiemClaims, 'per diem')} · ${plural(totals.expenseClaims, 'expense')}` },
        { label: 'Claimants', value: String(totals.claimants), hint: periodText },
      ]} />

      <Section title="By region" subtitle={periodText}>
        <GroupTable
          label="Region"
          rows={byRegion}
          nameOf={r => r.region}
          totals={totals}
          emptyText={`No final claims in ${periodText}.`}
        />
      </Section>

      <Section title="By job position" subtitle="The position on each claimant's profile. Largest first.">
        <GroupTable
          label="Job position"
          rows={byPosition}
          nameOf={p => p.position}
          totals={totals}
          emptyText={`No final claims in ${periodText}.`}
        />
      </Section>
    </>
  );
};

export default BreakdownTab;
